import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Plus, Pencil, Trash2, Star, StarOff, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { US_STATES } from '../lib/usStates';
import api, { employeeLicensesAPI } from '../lib/api';
import { useCap } from './auth/RequireCap';
import { FileText } from 'lucide-react';

// Driver's License and State Issued ID added 2026-06-08 — they participate in
// the expiration alert system identically but never feed the evaluation
// snapshot (the eval form filters to security-only types server-side).
// 2026-06: non-security credentials the compliance team also tracks. Kept in
// sync with LICENSE_TYPES in backend/employee_licenses.py.
const LICENSE_TYPES = ['Armed', 'Unarmed', 'PI', "Driver's License", 'State Issued ID',
  'Occupational License', 'Remote Pilot', 'Commission (Active)', 'Commission (Retired)',
  'Firearms Permit'];

const emptyDraft = {
  state: '',
  license_number: '',
  license_type: 'Armed',
  categories: '',
  expiration_date: '',
  notification_primary_days: 60,
  notification_secondary_days: 30,
};

/**
 * `employeeId` (live mode): API-backed CRUD against the persistent record.
 * `draftLicenses` + `onDraftChange` (draft mode, used during new-employee
 *   creation): the panel becomes a controlled component over an in-memory
 *   array. Parent flushes the buffered rows to the server after the employee
 *   is created. Pass `employeeId=null` to enable draft mode.
 */
export function LicensingPanel({ employeeId, draftLicenses, onDraftChange }) {
  const isDraftMode = !employeeId;
  // Cap gates. In draft mode (new-employee creation) the user has already
  // passed employees:add at the page level — draft-mode buttons stay visible
  // so the create flow isn't broken. In live mode each control matches the
  // backend gate it triggers (write-hole closure 2026-06-26).
  const canAdd = useCap('employees', 'edit');  // mirrors the API: adding a licence is an edit
  const canEdit = useCap('employees', 'edit');
  const canDelete = useCap('employees', 'delete');
  const showAdd = isDraftMode || canAdd;
  const showEdit = isDraftMode || canEdit;
  const showDelete = isDraftMode || canDelete;
  const [liveLicenses, setLiveLicenses] = useState([]);
  const licenses = isDraftMode ? (draftLicenses || []) : liveLicenses;
  const setLicenses = (next) => {
    if (isDraftMode) {
      onDraftChange && onDraftChange(next);
    } else {
      setLiveLicenses(next);
    }
  };

  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | license id (live) or index (draft)
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (isDraftMode) return;
    setLoading(true);
    try {
      const r = await employeeLicensesAPI.list(employeeId);
      setLiveLicenses(r.data);
    } catch (e) {
      toast.error('Failed to load licenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (employeeId) load(); /* eslint-disable-next-line */ }, [employeeId]);

  const openAdd = () => { setEditing('new'); setDraft({ ...emptyDraft }); };
  const openEdit = (lic, idx) => {
    setEditing(isDraftMode ? `idx-${idx}` : lic.id);
    setDraft({
      state: lic.state || '',
      license_number: lic.license_number || '',
      license_type: lic.license_type || 'Armed',
      categories: lic.categories || '',
      expiration_date: lic.expiration_date || '',
      notification_primary_days: lic.notification_primary_days ?? 60,
      notification_secondary_days: lic.notification_secondary_days ?? 30,
    });
  };
  const closeForm = () => { setEditing(null); setDraft(emptyDraft); };

  const submit = async () => {
    if (!draft.state || draft.state.length !== 2) { toast.error('Select a state'); return; }
    if (!draft.license_number?.trim()) { toast.error('License number is required'); return; }
    if (!LICENSE_TYPES.includes(draft.license_type)) { toast.error('Invalid license type'); return; }
    if (!draft.expiration_date) { toast.error('Expiration date is required'); return; }
    setSaving(true);
    try {
      if (isDraftMode) {
        // In-memory buffer — the parent flushes these via POST after the
        // employee record is created.
        if (editing === 'new') {
          const nextList = [...licenses];
          // First license in the buffer auto-becomes primary.
          const isPrimary = nextList.length === 0;
          nextList.push({ ...draft, is_primary: isPrimary });
          setLicenses(nextList);
          toast.success('License queued — will save with the new employee');
        } else {
          const idx = Number(String(editing).replace('idx-', ''));
          const nextList = licenses.map((l, i) => i === idx ? { ...l, ...draft } : l);
          setLicenses(nextList);
          toast.success('License updated');
        }
      } else if (editing === 'new') {
        await employeeLicensesAPI.create(employeeId, draft);
        toast.success('License added');
        await load();
      } else {
        await employeeLicensesAPI.update(employeeId, editing, draft);
        toast.success('License updated');
        await load();
      }
      closeForm();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (lic, idx) => {
    if (!window.confirm(`Delete ${lic.license_type} license #${lic.license_number}?`)) return;
    try {
      if (isDraftMode) {
        const nextList = licenses.filter((_, i) => i !== idx);
        // If we deleted the primary, promote the next-soonest-expiration.
        if (lic.is_primary && nextList.length > 0 && !nextList.some(l => l.is_primary)) {
          const sorted = [...nextList].sort((a, b) => (a.expiration_date || '').localeCompare(b.expiration_date || ''));
          const promote = sorted[0];
          const promoteIdx = nextList.findIndex(l => l === promote);
          if (promoteIdx >= 0) nextList[promoteIdx] = { ...nextList[promoteIdx], is_primary: true };
        }
        setLicenses(nextList);
        toast.success('License removed');
      } else {
        await employeeLicensesAPI.remove(employeeId, lic.id);
        toast.success('License deleted');
        await load();
      }
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const viewCredential = async (lic) => {
    try {
      const res = await api.get(employeeLicensesAPI.licenseCredentialFileUrl(employeeId, lic.id), { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.status === 403
        ? 'You do not have access to this credential file.'
        : 'Could not open the credential file.');
    }
  };

  const setPrimary = async (lic, idx) => {
    try {
      if (isDraftMode) {
        const nextList = licenses.map((l, i) => ({ ...l, is_primary: i === idx }));
        setLicenses(nextList);
        toast.success('Primary license updated');
      } else {
        await employeeLicensesAPI.setPrimary(employeeId, lic.id);
        toast.success('Primary license updated');
        await load();
      }
    } catch (e) {
      toast.error('Failed to set primary');
    }
  };

  const daysUntil = (iso) => {
    if (!iso) return null;
    const t = new Date(iso + 'T00:00:00');
    const today = new Date(); today.setHours(0,0,0,0);
    return Math.round((t - today) / 86400000);
  };

  return (
    <Card data-testid="employee-licensing-card" className="lg:col-span-3">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> Licensing</CardTitle>
        {showAdd && (
          <Button size="sm" onClick={openAdd} data-testid="employee-license-add"><Plus className="w-4 h-4 mr-1" /> Add License</Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {isDraftMode && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="employee-license-draft-hint">
            Licenses you add here are queued in this draft. They&apos;ll be saved when you click <strong>Save Employee</strong>.
          </p>
        )}
        {loading && <p className="text-sm text-slate-500">Loading…</p>}
        {!loading && licenses.length === 0 && editing !== 'new' && (
          showAdd
            ? <p className="text-sm text-slate-500">No licenses on file. Use <strong>Add License</strong> to record one.</p>
            : <p className="text-sm text-slate-500" data-testid="employee-license-empty-readonly">No licenses on file.</p>
        )}
        {licenses.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>State</TableHead>
                <TableHead>License #</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Categories</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Lead Times</TableHead>
                {(showEdit || showDelete) && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {licenses.map((lic, idx) => {
                const days = daysUntil(lic.expiration_date);
                const expiringSoon = days !== null && days <= (lic.notification_primary_days || 60);
                const expired = days !== null && days < 0;
                const rowKey = lic.id || `draft-${idx}`;
                return (
                  <TableRow key={rowKey} data-testid={`employee-license-row-${rowKey}`}>
                    <TableCell>
                      {lic.is_primary ? (
                        <Star className="w-4 h-4 fill-amber-400 stroke-amber-500" aria-label="Primary license" />
                      ) : showEdit ? (
                        <button onClick={() => setPrimary(lic, idx)} title="Set as primary" data-testid={`employee-license-make-primary-${rowKey}`}>
                          <StarOff className="w-4 h-4 stroke-slate-300 hover:stroke-amber-500" />
                        </button>
                      ) : (
                        <StarOff className="w-4 h-4 stroke-slate-200" aria-label="Not primary" />
                      )}
                    </TableCell>
                    <TableCell className="font-mono">{lic.state}</TableCell>
                    <TableCell className="font-mono">{lic.license_number}</TableCell>
                    <TableCell><Badge variant="outline">{lic.license_type}</Badge></TableCell>
                    <TableCell className="text-sm text-fg-muted max-w-[260px]" title={lic.categories || ''}>
                      <div className="truncate">{lic.categories || '—'}</div>
                      {lic.credential_source && (
                        <div className="mt-1 flex items-center gap-2 text-xs text-slate-500" data-testid={`employee-license-provenance-${rowKey}`}>
                          <ShieldCheck className="w-3 h-3 text-emerald-600 shrink-0" />
                          <span className="truncate" title={lic.credential_source}>{lic.credential_source}</span>
                          {lic.can_view_credential_file && (
                            <button type="button" onClick={() => viewCredential(lic)}
                                    className="inline-flex items-center gap-1 text-blue-600 hover:underline shrink-0"
                                    data-testid={`employee-license-view-credential-${rowKey}`}>
                              <FileText className="w-3 h-3" /> View
                            </button>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={expired ? 'text-red-600 font-medium' : expiringSoon ? 'text-amber-600 font-medium' : ''}>
                        {lic.expiration_date}
                      </span>
                      {days !== null && (
                        <span className="text-xs text-slate-500 ml-2">
                          {expired ? `(${Math.abs(days)}d ago)` : `(${days}d)`}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">
                      {lic.notification_primary_days}d · {lic.notification_secondary_days}d
                    </TableCell>
                    {(showEdit || showDelete) && (
                      <TableCell className="text-right space-x-1">
                        {showEdit && <Button size="sm" variant="ghost" onClick={() => openEdit(lic, idx)} data-testid={`employee-license-edit-${rowKey}`}><Pencil className="w-4 h-4" /></Button>}
                        {showDelete && <Button size="sm" variant="ghost" onClick={() => remove(lic, idx)} data-testid={`employee-license-delete-${rowKey}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {editing && (
          <div className="border border-slate-200 rounded-lg p-4 space-y-4 bg-slate-50" data-testid="employee-license-form">
            <h4 className="font-semibold">{editing === 'new' ? 'Add License' : 'Edit License'}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>State</Label>
                <Select value={draft.state} onValueChange={(v) => setDraft({ ...draft, state: v })}>
                  <SelectTrigger data-testid="employee-license-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {US_STATES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>License Type</Label>
                <Select value={draft.license_type} onValueChange={(v) => setDraft({ ...draft, license_type: v })}>
                  <SelectTrigger data-testid="employee-license-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LICENSE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>License Number</Label>
                <Input value={draft.license_number} onChange={(e) => setDraft({ ...draft, license_number: e.target.value })} className="font-mono" data-testid="employee-license-number" />
              </div>
              <div className="space-y-2">
                <Label>Expiration Date</Label>
                <Input type="date" value={draft.expiration_date} onChange={(e) => setDraft({ ...draft, expiration_date: e.target.value })} data-testid="employee-license-exp" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Categories <span className="text-fg-muted font-normal">(optional)</span></Label>
                <Input value={draft.categories} placeholder="e.g. Class D, Baton, Taser"
                       onChange={(e) => setDraft({ ...draft, categories: e.target.value })}
                       data-testid="employee-license-categories" />
              </div>
              <div className="space-y-2">
                <Label>Primary Notification (days before)</Label>
                <Input type="number" min="0" value={draft.notification_primary_days} onChange={(e) => setDraft({ ...draft, notification_primary_days: Number(e.target.value) || 0 })} data-testid="employee-license-notif-primary" />
              </div>
              <div className="space-y-2">
                <Label>Secondary Notification (days before)</Label>
                <Input type="number" min="0" value={draft.notification_secondary_days} onChange={(e) => setDraft({ ...draft, notification_secondary_days: Number(e.target.value) || 0 })} data-testid="employee-license-notif-secondary" />
              </div>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <Button variant="outline" onClick={closeForm} disabled={saving} data-testid="employee-license-cancel">Cancel</Button>
              <Button onClick={submit} disabled={saving} data-testid="employee-license-save">{saving ? 'Saving…' : (editing === 'new' ? 'Add' : 'Save')}</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
